// src/components/schedule/HourlyAvailabilityGrid.tsx
// Date-based availability grid - set availability for each day in the next 2 weeks
import React, { useState, useEffect, useRef } from 'react';
import { Check, ChevronLeft, ChevronRight, Copy, Save } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { format, addDays, isSameDay, isToday, isBefore, startOfDay } from 'date-fns';

// Inner availability map. Keys are slot start times like "09:00" / "09:30".
// Legacy numeric-hour keys ("9") are accepted on read and auto-expanded.
interface DayAvailability {
  [slotKey: string]: boolean;
}

// Date-based availability: "2024-01-15" -> { "09:00": true, "09:30": true, ... }
export interface DateAvailability {
  [dateStr: string]: DayAvailability;
}

// Legacy weekly format for backward compatibility
export interface WeeklyAvailability {
  [dayOfWeek: number]: DayAvailability;
}

interface HourlyAvailabilityGridProps {
  initialAvailability?: DateAvailability | WeeklyAvailability;
  onSave?: (availability: DateAvailability) => Promise<void>;
  saving?: boolean;
  daysAhead?: number; // How many days to show (default 14)
  readOnly?: boolean; // If true, user cannot edit, just view
  userName?: string; // Name to display when viewing someone else's schedule
}

// Default business hours: 8 AM - 8 PM
const START_HOUR = 8;
const END_HOUR = 20;
const SLOT_MINUTES = 30; // 30-minute granularity

// Build all slot keys ("09:00", "09:30", ...) for the configured day window.
const buildDaySlotKeys = (): string[] => {
  const keys: string[] = [];
  for (let h = START_HOUR; h < END_HOUR; h++) {
    for (let m = 0; m < 60; m += SLOT_MINUTES) {
      keys.push(`${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`);
    }
  }
  return keys;
};

const DAY_SLOT_KEYS = buildDaySlotKeys();

const formatSlot = (slotKey: string): string => {
  const [hStr, mStr] = slotKey.split(':');
  const hour = Number(hStr);
  const min = Number(mStr);
  const ampm = hour >= 12 ? 'PM' : 'AM';
  const displayHour = hour > 12 ? hour - 12 : hour === 0 ? 12 : hour;
  return min === 0 ? `${displayHour}${ampm}` : `${displayHour}:${mStr}${ampm}`;
};

// Normalize a possibly-legacy DayAvailability into string-keyed slots.
// Legacy whole-hour keys ("9") expand into both :00 and :30 slots for that hour.
const normalizeDayAvailability = (raw: DayAvailability | undefined): DayAvailability => {
  if (!raw) return {};
  const out: DayAvailability = {};
  for (const key of Object.keys(raw)) {
    if (!raw[key]) continue;
    if (key.includes(':')) {
      out[key] = true;
    } else {
      // Legacy numeric hour: expand into all sub-slots covering that hour.
      const hour = Number(key);
      for (let m = 0; m < 60; m += SLOT_MINUTES) {
        out[`${hour.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`] = true;
      }
    }
  }
  return out;
};

// Check if availability is in legacy weekly format (keys are 0-6)
const isWeeklyFormat = (avail: any): avail is WeeklyAvailability => {
  const keys = Object.keys(avail);
  return keys.length > 0 && keys.every(k => Number(k) >= 0 && Number(k) <= 6);
};

// Convert weekly availability to date-based for the next N days
const weeklyToDateAvailability = (
  weekly: WeeklyAvailability,
  daysAhead: number
): DateAvailability => {
  const dateAvail: DateAvailability = {};
  const today = startOfDay(new Date());

  for (let i = 0; i < daysAhead; i++) {
    const date = addDays(today, i);
    const dateStr = format(date, 'yyyy-MM-dd');
    const dayOfWeek = date.getDay();

    if (weekly[dayOfWeek]) {
      dateAvail[dateStr] = normalizeDayAvailability(weekly[dayOfWeek]);
    } else {
      // Initialize empty
      const empty: DayAvailability = {};
      for (const slotKey of DAY_SLOT_KEYS) empty[slotKey] = false;
      dateAvail[dateStr] = empty;
    }
  }

  return dateAvail;
};

export const HourlyAvailabilityGrid: React.FC<HourlyAvailabilityGridProps> = ({
  initialAvailability,
  onSave,
  saving = false,
  daysAhead = 56,
  readOnly = false,
  userName
}) => {
  const today = startOfDay(new Date());
  const dates = Array.from({ length: daysAhead }, (_, i) => addDays(today, i));

  const [selectedDateIndex, setSelectedDateIndex] = useState<number>(0);
  const [availability, setAvailability] = useState<DateAvailability>(() => {
    // Initialize with empty availability for all dates
    const initial: DateAvailability = {};
    dates.forEach(date => {
      const dateStr = format(date, 'yyyy-MM-dd');
      const empty: DayAvailability = {};
      for (const slotKey of DAY_SLOT_KEYS) empty[slotKey] = false;
      initial[dateStr] = empty;
    });
    return initial;
  });
  const [hasChanges, setHasChanges] = useState(false);
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const selectedDate = dates[selectedDateIndex];
  const selectedDateStr = format(selectedDate, 'yyyy-MM-dd');

  // Update if initial availability changes
  useEffect(() => {
    if (initialAvailability) {
      let dateAvail: DateAvailability;

      if (isWeeklyFormat(initialAvailability)) {
        // Convert legacy weekly format to date-based
        dateAvail = weeklyToDateAvailability(initialAvailability, daysAhead);
      } else {
        // Already date-based, merge with our initialized dates.
        // Normalize each day so legacy whole-hour keys expand into 30-min slots.
        dateAvail = { ...availability };
        Object.keys(initialAvailability).forEach(dateStr => {
          if (dateAvail[dateStr]) {
            dateAvail[dateStr] = normalizeDayAvailability(
              (initialAvailability as DateAvailability)[dateStr]
            );
          }
        });
      }

      setAvailability(dateAvail);
      setHasChanges(false);
    }
  }, [initialAvailability]);

  const toggleSlot = (slotKey: string) => {
    if (readOnly) return;
    setAvailability(prev => ({
      ...prev,
      [selectedDateStr]: {
        ...prev[selectedDateStr],
        [slotKey]: !prev[selectedDateStr]?.[slotKey]
      }
    }));
    setHasChanges(true);
  };

  const selectAllDay = () => {
    setAvailability(prev => {
      const newDayAvail: DayAvailability = {};
      for (const slotKey of DAY_SLOT_KEYS) newDayAvail[slotKey] = true;
      return { ...prev, [selectedDateStr]: newDayAvail };
    });
    setHasChanges(true);
  };

  const clearDay = () => {
    setAvailability(prev => {
      const newDayAvail: DayAvailability = {};
      for (const slotKey of DAY_SLOT_KEYS) newDayAvail[slotKey] = false;
      return { ...prev, [selectedDateStr]: newDayAvail };
    });
    setHasChanges(true);
  };

  const copyToAllWeekdays = () => {
    const currentDayAvail = availability[selectedDateStr] || {};
    setAvailability(prev => {
      const newAvail = { ...prev };
      // Copy to all future weekdays (Mon-Fri)
      dates.forEach(date => {
        const dayOfWeek = date.getDay();
        if (dayOfWeek >= 1 && dayOfWeek <= 5) { // Monday-Friday
          const dateStr = format(date, 'yyyy-MM-dd');
          newAvail[dateStr] = { ...currentDayAvail };
        }
      });
      return newAvail;
    });
    setHasChanges(true);
    toast.success('Copied to all weekdays');
  };

  const handleSave = async () => {
    try {
      await onSave(availability);
      setHasChanges(false);
      toast.success('Availability saved');
    } catch (error) {
      console.error('Error saving availability:', error);
      toast.error('Failed to save availability');
    }
  };

  const scrollDates = (direction: 'left' | 'right') => {
    if (direction === 'left' && selectedDateIndex > 0) {
      setSelectedDateIndex(prev => prev - 1);
    } else if (direction === 'right' && selectedDateIndex < dates.length - 1) {
      setSelectedDateIndex(prev => prev + 1);
    }
  };

  // Number of selected 30-min slots for a given date.
  const getSelectedSlotCount = (dateStr: string): number => {
    const dayAvail = availability[dateStr];
    if (!dayAvail) return 0;
    return Object.values(dayAvail).filter(Boolean).length;
  };

  // Selected slot count rendered as "Xh Ym" (or "Xm" / "Xh") for the overview.
  const formatSelectedDuration = (dateStr: string): string => {
    const minutes = getSelectedSlotCount(dateStr) * SLOT_MINUTES;
    if (minutes === 0) return '0h';
    const h = Math.floor(minutes / 60);
    const m = minutes % 60;
    if (h === 0) return `${m}m`;
    if (m === 0) return `${h}h`;
    return `${h}h ${m}m`;
  };

  const slotKeys = DAY_SLOT_KEYS;

  // Calculate visible date range for the scrollable tabs
  const visibleCount = 7; // Show 7 dates at a time
  const startVisibleIndex = Math.max(0, Math.min(selectedDateIndex - 3, dates.length - visibleCount));
  const visibleDates = dates.slice(startVisibleIndex, startVisibleIndex + visibleCount);

  return (
    <div className="space-y-4">
      {/* Date selector with horizontal scroll */}
      <div className="flex items-center gap-2">
        <button
          onClick={() => scrollDates('left')}
          disabled={selectedDateIndex === 0}
          className="p-2 rounded-lg hover:bg-gray-100 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
        >
          <ChevronLeft className="h-5 w-5" />
        </button>

        <div
          ref={scrollContainerRef}
          className="flex-1 overflow-x-auto scrollbar-hide"
        >
          <div className="flex space-x-1 min-w-max">
            {visibleDates.map((date) => {
              const dateStr = format(date, 'yyyy-MM-dd');
              const slotCount = getSelectedSlotCount(dateStr);
              const isSelected = isSameDay(date, selectedDate);
              const dateIsToday = isToday(date);

              return (
                <button
                  key={dateStr}
                  onClick={() => setSelectedDateIndex(dates.findIndex(d => isSameDay(d, date)))}
                  className={`relative flex flex-col items-center px-3 py-2 rounded-lg text-sm font-medium transition-colors min-w-[70px] ${
                    isSelected
                      ? 'bg-indigo-600 text-white shadow-md'
                      : dateIsToday
                      ? 'bg-indigo-100 text-indigo-700 hover:bg-indigo-200'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  <span className="text-xs opacity-75">
                    {format(date, 'EEE')}
                  </span>
                  <span className="text-base font-semibold">
                    {format(date, 'MMM d')}
                  </span>
                  {slotCount > 0 && (
                    <span className={`absolute -top-1 -right-1 min-w-[1.25rem] h-5 px-1 text-[10px] rounded-full flex items-center justify-center ${
                      isSelected ? 'bg-green-400 text-green-900' : 'bg-green-500 text-white'
                    }`}>
                      {slotCount}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        <button
          onClick={() => scrollDates('right')}
          disabled={selectedDateIndex === dates.length - 1}
          className="p-2 rounded-lg hover:bg-gray-100 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
        >
          <ChevronRight className="h-5 w-5" />
        </button>
      </div>

      {/* Quick actions */}
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-medium text-gray-900">
          {format(selectedDate, 'EEEE, MMMM d')}
          {isToday(selectedDate) && (
            <span className="ml-2 text-sm font-normal text-indigo-600">(Today)</span>
          )}
          {userName && (
            <span className="ml-2 text-sm font-normal text-gray-500">- {userName}'s Availability</span>
          )}
        </h3>
        {!readOnly && (
          <div className="flex space-x-2">
            <button
              onClick={selectAllDay}
              className="px-3 py-1 text-sm text-green-600 hover:bg-green-50 rounded-md transition-colors"
            >
              Select All
            </button>
            <button
              onClick={clearDay}
              className="px-3 py-1 text-sm text-red-600 hover:bg-red-50 rounded-md transition-colors"
            >
              Clear
            </button>
            <button
              onClick={copyToAllWeekdays}
              className="px-3 py-1 text-sm text-indigo-600 hover:bg-indigo-50 rounded-md transition-colors flex items-center gap-1"
            >
              <Copy className="h-3 w-3" />
              Copy to Weekdays
            </button>
          </div>
        )}
      </div>

      {/* 30-minute slot grid */}
      <div className="grid grid-cols-4 sm:grid-cols-6 gap-2">
        {slotKeys.map((slotKey) => {
          const isAvailable = availability[selectedDateStr]?.[slotKey] || false;
          return (
            <button
              key={slotKey}
              onClick={() => toggleSlot(slotKey)}
              disabled={readOnly}
              className={`
                relative p-3 rounded-lg border-2 transition-all duration-150
                ${readOnly ? 'cursor-default' : 'cursor-pointer'}
                ${isAvailable
                  ? 'bg-green-100 border-green-500 text-green-800' + (!readOnly ? ' hover:bg-green-200' : '')
                  : 'bg-gray-50 border-gray-200 text-gray-600' + (!readOnly ? ' hover:border-gray-300 hover:bg-gray-100' : '')
                }
              `}
            >
              <span className="text-sm font-medium">{formatSlot(slotKey)}</span>
              {isAvailable && (
                <Check className="absolute top-1 right-1 h-3 w-3 text-green-600" />
              )}
            </button>
          );
        })}
      </div>

      {/* Multi-week overview */}
      <div className="bg-gray-50 rounded-lg p-4">
        <h4 className="text-sm font-medium text-gray-700 mb-3">{Math.ceil(dates.length / 7)}-Week Overview</h4>
        <div className="max-h-72 overflow-y-auto space-y-1">
          {Array.from({ length: Math.ceil(dates.length / 7) }, (_, w) => (
            <div key={w} className="grid grid-cols-7 gap-1 text-center text-xs">
              {dates.slice(w * 7, w * 7 + 7).map((date) => {
                const dateStr = format(date, 'yyyy-MM-dd');
                const count = getSelectedSlotCount(dateStr);
                const durationLabel = formatSelectedDuration(dateStr);
                const isSelected = isSameDay(date, selectedDate);
                return (
                  <button
                    key={dateStr}
                    onClick={() => setSelectedDateIndex(dates.findIndex(d => isSameDay(d, date)))}
                    className={`p-2 rounded transition-colors ${
                      isSelected ? 'ring-2 ring-indigo-500' : ''
                    } ${
                      count > 0 ? 'bg-green-100 hover:bg-green-200' : 'bg-white hover:bg-gray-100'
                    }`}
                  >
                    <div className="font-medium text-gray-600">{format(date, 'EEE')}</div>
                    <div className="text-gray-500">{format(date, 'd')}</div>
                    <div className={`mt-1 ${count > 0 ? 'text-green-700 font-medium' : 'text-gray-400'}`}>
                      {durationLabel}
                    </div>
                  </button>
                );
              })}
            </div>
          ))}
        </div>
      </div>

      {/* Save button */}
      {!readOnly && hasChanges && onSave && (
        <div className="flex justify-end pt-4 border-t">
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex items-center px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:opacity-50 transition-colors"
          >
            <Save className="h-4 w-4 mr-2" />
            {saving ? 'Saving...' : 'Save Availability'}
          </button>
        </div>
      )}
    </div>
  );
};

export default HourlyAvailabilityGrid;
